import React, { useEffect, useState, useRef, useMemo } from "react"
import { withRouter, Link } from "react-router-dom"

import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  FormFeedback,
  UncontrolledTooltip,
  Input,
  Form,
  FormGroup,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import ReactSelect from "react-select"
import ReactSwitch from "react-switch"
import { isEmpty } from "lodash"

//Import Components
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import TableContainer from "components/Common/TableContainer"
import { Genders, MultiLevelApprovals, Religions } from "constants/types"

//redux
import { useSelector, useDispatch } from "react-redux"
import {
  getAttendanceTypes as onGetAttendanceTypes,
  addNewAttendanceType as onAddNewAttendanceType,
  updateAttendanceType as onUpdateAttendanceType,
  deleteAttendanceType as onDeleteAttendanceType,
} from "store/actions"

const AttendanceTypesList = props => {
  //meta title
  document.title = "Attendance Types | React Admin & Dashboard Template"

  const dispatch = useDispatch()
  const [attendanceType, setAttendanceType] = useState()

  const EMPTY_LEAVE_TYPE = {
    is_active: attendanceType?.is_active ?? true,
    name: attendanceType?.name ?? "",
    attachments: attendanceType?.attachments ?? 0,
    limit: attendanceType?.limit ?? 1,
    limit_hours: attendanceType?.limit_hours ?? false,
    gender: attendanceType?.gender ?? "",
    multi_level_approval: attendanceType?.multi_level_approval ?? false,
    note: attendanceType?.note ?? [],
    remotely: attendanceType?.remotely ?? false,
    religion: attendanceType?.religion ?? "",
    renew_after_days: attendanceType?.renew_after_days ?? 1,
    require_clock: attendanceType?.require_clock ?? false,
    unlock_after_months: attendanceType?.unlock_after_months ?? 0,
    level_approvals: attendanceType?.level_approvals ?? [],
  }

  const LEAVE_TYPE_VALIDATION = {
    name: Yup.string().required("Please enter name"),
    attachments: Yup.number().required(),
    limit: Yup.number().required(),
    limit_hours: Yup.bool().required(),
    gender: Yup.string(),
    multi_level_approval: Yup.bool().required(),
    note: Yup.object().nullable(),
    remotely: Yup.bool().required(),
    religion: Yup.string(),
    renew_after_days: Yup.number().required(),
    require_clock: Yup.bool().required(),
    unlock_after_months: Yup.number().required(),
  }

  // validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: EMPTY_LEAVE_TYPE,
    validationSchema: Yup.object(LEAVE_TYPE_VALIDATION),
    onSubmit: values => {
      const tmp = {
        id: attendanceType.id,
        ...values,
      }
      if (isEdit) {
        const updateAttendanceType = tmp
        // update attendanceType
        dispatch(onUpdateAttendanceType(updateAttendanceType))
        // validation.resetForm()
        // setIsEdit(false)
      } else {
        const newAttendanceType = tmp
        // save new attendanceType
        dispatch(onAddNewAttendanceType(newAttendanceType))
        // validation.resetForm()
      }
      // toggle()
    },
  })

  const { attendanceTypes, loading } = useSelector(state => ({
    attendanceTypes: state.AttendanceTypes.attendanceTypes,
    loading: state.apiGlobal.loading,
  }))

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Name",
        accessor: "name",
        filterable: true,
      },
      {
        Header: "Status",
        accessor: "is_active",
        filterable: true,
        Cell: cellProps =>
          cellProps.value !== undefined && (
            <ReactSwitch
              disabled={loading}
              onChange={() => {
                dispatch(
                  onUpdateAttendanceType({
                    ...cellProps.row.original,
                    is_active: !cellProps.value,
                  })
                )
              }}
              checked={cellProps.value ?? false}
            />
          ),
      },
      {
        Header: "Action",
        Cell: cellProps => {
          return (
            <div className="d-flex gap-3">
              <Link
                to="#"
                className="text-success"
                onClick={() => {
                  const attendanceTypeData = cellProps.row.original
                  handleAttendanceTypeClick(attendanceTypeData)
                }}
              >
                <i className="mdi mdi-pencil font-size-18" id="edittooltip" />
                <UncontrolledTooltip placement="top" target="edittooltip">
                  Edit
                </UncontrolledTooltip>
              </Link>
              <Link
                to="#"
                className="text-danger"
                onClick={() => {
                  const attendanceTypeData = cellProps.row.original
                  onClickDelete(attendanceTypeData)
                }}
              >
                <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
                <UncontrolledTooltip placement="top" target="deletetooltip">
                  Delete
                </UncontrolledTooltip>
              </Link>
            </div>
          )
        },
      },
    ],
    [loading]
  )

  useEffect(() => {
    if (attendanceTypes && !attendanceTypes.length) {
      setIsEdit(false)
    }
  }, [dispatch, attendanceTypes])

  useEffect(() => {
    dispatch(onGetAttendanceTypes())
  }, [dispatch])

  useEffect(() => {
    setAttendanceType(attendanceTypes)
    setIsEdit(false)
  }, [attendanceTypes])

  useEffect(() => {
    if (!isEmpty(attendanceTypes) && !!isEdit) {
      setAttendanceType(attendanceTypes)
      setIsEdit(false)
    }
  }, [attendanceTypes])

  const toggle = () => {
    setModal(!modal)
  }

  const handleAttendanceTypeClick = arg => {
    const attendanceType = {
      ...arg,
    }

    setAttendanceType(attendanceType)
    setIsEdit(true)

    toggle()
  }

  var node = useRef()
  const onPaginationPageChange = page => {
    if (
      node &&
      node.current &&
      node.current.props &&
      node.current.props.pagination &&
      node.current.props.pagination.options
    ) {
      node.current.props.pagination.options.onPageChange(page)
    }
  }

  //delete customer
  const [deleteModal, setDeleteModal] = useState(false)

  const onClickDelete = attendanceTypes => {
    setAttendanceType(attendanceTypes)
    setDeleteModal(true)
  }

  const handleDeleteAttendanceType = () => {
    dispatch(onDeleteAttendanceType(attendanceType.id))
    onPaginationPageChange(1)
    setDeleteModal(false)
  }

  const handleAddAttendanceTypeClicks = () => {
    setIsEdit(false)
    toggle()
  }

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteAttendanceType}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs
            title="AttendanceTypes"
            breadcrumbItem="Attendance Type List"
          />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={attendanceTypes}
                    isGlobalFilter={true}
                    isAddOptions="Attendance Type"
                    handleAddOptionClick={handleAddAttendanceTypeClicks}
                    customPageSize={10}
                    className="custom-header-css"
                  />

                  <Modal isOpen={modal} toggle={toggle} size="lg">
                    <ModalHeader toggle={toggle} tag="h4">
                      {!!isEdit ? "Edit AttendanceType" : "Add AttendanceType"}
                    </ModalHeader>
                    <ModalBody>
                      <Form
                        onSubmit={e => {
                          e.preventDefault()
                          validation.handleSubmit()
                          return false
                        }}
                      >
                        <Row>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label className="form-label">Name</Label>
                              <Input
                                name="name"
                                label="Name"
                                type="text"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.name}
                                invalid={
                                  validation.touched.name &&
                                  validation.errors.name
                                    ? true
                                    : false
                                }
                              />
                              <FormFeedback type="invalid">
                                {validation.errors.name}
                              </FormFeedback>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label className="form-label">
                                Unlock after (months)
                              </Label>
                              <Input
                                name="unlock_after_months"
                                type="number"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.unlock_after_months}
                                invalid={
                                  validation.touched.unlock_after_months &&
                                  validation.errors.unlock_after_months
                                    ? true
                                    : false
                                }
                              />
                              <FormFeedback type="invalid">
                                {validation.errors.unlock_after_months}
                              </FormFeedback>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label className="form-label">
                                Require Clock in/out
                              </Label>
                              <FormGroup>
                                <ReactSwitch
                                  onChange={() => {
                                    validation.setFieldValue(
                                      "require_clock",
                                      !validation.values.require_clock
                                    )
                                  }}
                                  checked={validation.values.require_clock}
                                />
                                <FormFeedback type="invalid">
                                  {validation.errors.require_clock}
                                </FormFeedback>
                              </FormGroup>
                            </div>
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">Remotely</Label>
                            <FormGroup>
                              <ReactSwitch
                                onChange={() => {
                                  validation.setFieldValue(
                                    "remotely",
                                    !validation.values.remotely
                                  )
                                }}
                                checked={validation.values.remotely}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">Unlimited</Label>
                            <FormGroup>
                              <ReactSwitch
                                onChange={() => {
                                  validation.setFieldValue(
                                    "limit",
                                    validation.values.limit > 0 ? 0 : 1
                                  )
                                }}
                                checked={validation.values.limit === 0}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">
                              Multi-level approval
                            </Label>
                            <FormGroup>
                              <ReactSwitch
                                onChange={() => {
                                  validation.setFieldValue(
                                    "multi_level_approval",
                                    !validation.values.multi_level_approval
                                  )
                                }}
                                checked={validation.values.multi_level_approval}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">
                              Require attachments
                            </Label>
                            <FormGroup>
                              <ReactSwitch
                                onChange={() => {
                                  validation.setFieldValue(
                                    "attachments",
                                    validation.values.attachments > 0 ? 0 : 1
                                  )
                                }}
                                checked={validation.values.attachments > 0}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">Religion based</Label>
                            <FormGroup>
                              <ReactSwitch
                                onChange={() => {
                                  validation.setFieldValue(
                                    "religion",
                                    validation.values.religion
                                      ? undefined
                                      : "muslim"
                                  )
                                }}
                                checked={!isEmpty(validation.values.religion)}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">Gender based</Label>
                            <FormGroup>
                              <ReactSwitch
                                onChange={() => {
                                  validation.setFieldValue(
                                    "gender",
                                    validation.values.gender
                                      ? undefined
                                      : "male"
                                  )
                                }}
                                checked={!isEmpty(validation.values.gender)}
                              />
                            </FormGroup>
                          </Col>
                          {validation.values.limit > 0 && (
                            <Col md={6}>
                              <Label className="form-label">
                                {validation.values.limit_hours
                                  ? "Hours"
                                  : "Days"}
                              </Label>
                              <FormGroup>
                                <ReactSwitch
                                  onChange={() => {
                                    validation.setFieldValue(
                                      "limit_hours",
                                      !validation.values.limit_hours
                                    )
                                  }}
                                  checked={validation.values.limit_hours}
                                />
                                <FormFeedback type="invalid">
                                  {validation.errors.limit_hours}
                                </FormFeedback>
                              </FormGroup>
                            </Col>
                          )}
                        </Row>
                        <Row>
                          {validation.values.attachments > 0 && (
                            <Col md={6}>
                              <FormGroup>
                                <Label className="form-label">
                                  Require attachments count
                                </Label>
                                <Input
                                  name="attachments"
                                  type="number"
                                  min="1"
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  value={validation.values.attachments}
                                  invalid={
                                    validation.touched.attachments &&
                                    validation.errors.attachments
                                      ? true
                                      : false
                                  }
                                />
                                <FormFeedback type="invalid">
                                  {validation.errors.attachments}
                                </FormFeedback>
                              </FormGroup>
                            </Col>
                          )}
                          {validation.values.religion && (
                            <Col md={6}>
                              <FormGroup>
                                <Label className="form-label">Religion</Label>
                                <ReactSelect
                                  isSearchable
                                  options={Religions}
                                  value={Religions.find(
                                    ct =>
                                      ct.value === validation.values.religion
                                  )}
                                  onChange={option =>
                                    validation.setFieldValue(
                                      "religion",
                                      option?.value
                                    )
                                  }
                                  placeholder="Select Type"
                                  className={
                                    validation.errors.religion &&
                                    validation.touched.religion
                                      ? " is-invalid form-control p-0 pe-5"
                                      : ""
                                  }
                                />
                                <FormFeedback type="invalid">
                                  {validation.errors.religion}
                                </FormFeedback>
                              </FormGroup>
                            </Col>
                          )}
                          {validation.values.gender && (
                            <Col md={6}>
                              <FormGroup>
                                <Label className="form-label">Gender</Label>
                                <ReactSelect
                                  isSearchable
                                  options={Genders}
                                  value={Genders.find(
                                    ct => ct.value === validation.values.gender
                                  )}
                                  onChange={option =>
                                    validation.setFieldValue(
                                      "gender",
                                      option?.value
                                    )
                                  }
                                  placeholder="Select Type"
                                  className={
                                    validation.errors.gender &&
                                    validation.touched.gender
                                      ? " is-invalid form-control p-0 pe-5"
                                      : ""
                                  }
                                />
                                <FormFeedback type="invalid">
                                  {validation.errors.gender}
                                </FormFeedback>
                              </FormGroup>
                            </Col>
                          )}
                          {validation.values.limit > 0 && (
                            <>
                              <Col md={6}>
                                <FormGroup>
                                  <Label className="form-label">
                                    Limit (
                                    {validation.values.limit_hours
                                      ? "Hours"
                                      : "Days"}
                                    )
                                  </Label>
                                  <Input
                                    name="limit"
                                    type="number"
                                    min="1"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.limit}
                                    invalid={
                                      validation.touched.limit &&
                                      validation.errors.limit
                                        ? true
                                        : false
                                    }
                                  />
                                  <FormFeedback type="invalid">
                                    {validation.errors.limit}
                                  </FormFeedback>
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Label className="form-label">
                                    Renew after(days)
                                  </Label>
                                  <Input
                                    name="renew_after_days"
                                    type="number"
                                    min="1"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.renew_after_days}
                                    invalid={
                                      validation.touched.renew_after_days &&
                                      validation.errors.renew_after_days
                                        ? true
                                        : false
                                    }
                                  />
                                  <FormFeedback type="invalid">
                                    {validation.errors.renew_after_days}
                                  </FormFeedback>
                                </FormGroup>
                              </Col>
                            </>
                          )}
                          {validation.values.multi_level_approval && (
                            <>
                              <Col md={6}>
                                <FormGroup>
                                  <Label className="form-label">
                                    Level One
                                  </Label>
                                  <ReactSelect
                                    isSearchable
                                    options={MultiLevelApprovals}
                                    value={MultiLevelApprovals.find(
                                      mla =>
                                        mla.value ===
                                        validation.values.level_approvals[0]
                                          ?.value
                                    )}
                                    onChange={option => {
                                      validation.setFieldValue(
                                        "level_approvals",
                                        [{ ...option, level: 1 }]
                                      )
                                    }}
                                    placeholder="Select Type"
                                    className={
                                      validation.errors.level_approvals &&
                                      validation.touched.level_approvals
                                        ? " is-invalid form-control p-0 pe-5"
                                        : ""
                                    }
                                  />
                                  <FormFeedback type="invalid">
                                    {validation.errors.level_approvals}
                                  </FormFeedback>
                                </FormGroup>
                                <FormGroup>
                                  <Label className="form-label">
                                    Level Two
                                  </Label>
                                  <ReactSelect
                                    isSearchable
                                    options={MultiLevelApprovals.filter(
                                      ({ value: id1 }) =>
                                        !validation.values.level_approvals.some(
                                          ({ value: id2 }) => id2 === id1
                                        )
                                    )}
                                    value={MultiLevelApprovals.find(
                                      mla =>
                                        mla.value ===
                                        validation.values.level_approvals[1]
                                          ?.value
                                    )}
                                    onChange={option => {
                                      let tmp = [
                                        ...validation.values.level_approvals,
                                      ]
                                      tmp[1] = {
                                        ...option,
                                        level: 2,
                                      }
                                      validation.setFieldValue(
                                        "level_approvals",
                                        tmp
                                      )
                                    }}
                                    placeholder="Select Type"
                                    className={
                                      validation.errors.level_approvals &&
                                      validation.touched.level_approvals
                                        ? " is-invalid form-control p-0 pe-5"
                                        : ""
                                    }
                                  />
                                  <FormFeedback type="invalid">
                                    {validation.errors.level_approvals}
                                  </FormFeedback>
                                </FormGroup>
                              </Col>
                            </>
                          )}
                        </Row>
                        <Row>
                          <Col>
                            <div className="text-end">
                              <button
                                type="submit"
                                className="btn btn-success save-attendanceType"
                              >
                                Save
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </ModalBody>
                  </Modal>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withRouter(AttendanceTypesList)
